<script lang="ts">
  // Popup menu: transparent scrim, `role=menu` panel positioned from an anchor (`menuPos`),
  // focus trap, arrow-key cycling (`menuKeys`) and Escape → close (spec §17 "menus").
  import type { Snippet } from 'svelte';
  import { focusTrap, menuKeys, menuPos, type MenuAnchor } from '$lib/util/dom';

  let {
    anchor,
    label = '',
    cls = '',
    onclose,
    children,
  }: {
    anchor: MenuAnchor | null;
    label?: string;
    cls?: string;
    onclose: () => void;
    children: Snippet;
  } = $props();

  let pos = $state({ top: -9999, left: -9999 });

  function onKey(ev: KeyboardEvent) {
    if (ev.key === 'Escape') {
      ev.preventDefault();
      ev.stopPropagation();
      onclose();
    }
  }
</script>

<div class="menu-scrim" role="presentation" aria-hidden="true" onclick={onclose}></div>
<!-- svelte-ignore a11y_no_noninteractive_element_interactions -->
<div
  class="selmenu {cls}"
  role="menu"
  aria-label={label || undefined}
  tabindex="-1"
  style="top:{pos.top}px;left:{pos.left}px;max-height:calc(100dvh - 16px)"
  use:focusTrap
  use:menuKeys
  use:menuPos={{ anchor, set: (p) => (pos = p) }}
  onkeydown={onKey}
>
  {@render children()}
</div>

<style>
  /* @css ui/Menu start */
  .menu-scrim {
    position: fixed;
    inset: 0;
    z-index: var(--z-overlay);
    background: transparent
  }

  .selmenu {
    position: fixed;
    z-index: var(--z-layer);
    width: var(--menu-w);
    overflow-y: auto;
    padding: var(--space-1);
    overscroll-behavior: contain;
    background: var(--surface-card);
    color: var(--text-body);
    border: 1px solid var(--border-subtle);
    border-radius: var(--radius-md);
    box-shadow: var(--shadow-lg);
    display: flex;
    flex-direction: column
  }

  .selmenu :global([role=menuitem]) {
    display: flex;
    align-items: center;
    gap: var(--space-3);
    padding: var(--space-3);
    min-block-size: var(--control-lg);
    background: none;
    border: 0;
    color: inherit;
    font: inherit;
    cursor: pointer;
    text-align: start;
    border-radius: var(--radius-control)
  }

  .selmenu :global([role=menuitem]:hover) {
    background: var(--surface-sunken)
  }

  .selmenu :global([role=menuitem]:active) {
    background: var(--mail-pressed)
  }
  /* @css ui/Menu end */
</style>
